import type { SpoInspectResult } from "@/api/admin/libraries_indexing"
import type { Library, LibraryConfig } from "@/api/admin-api"

import {
  type LibraryAuthConfig,
  MASKED_SECRET_INDICATOR,
  SHAREPOINT_COPY_SOURCE_TYPES,
  SHAREPOINT_LIST_SOURCE_TYPES,
  spoFieldStorageKey,
} from "../library-auth-utils"

/**
 * One form model for both the create and the edit dialog. `source_config` is kept as a loose
 * record because the indexer reads several key shapes (see library-auth-utils readSpoField).
 */
export type LibraryEditorMode = "create" | "edit"

export type LibrarySourceConfigDraft = Record<string, unknown> & { auth?: LibraryAuthConfig }

export type LibraryEditorValues = {
  id: string
  display_name: string
  description: string
  source_type: string
  source_config: LibrarySourceConfigDraft
  embedding_model_id: string
  index_name: string
  output_container: string
  output_table: string
  search_enabled: boolean
  indexing_enabled: boolean
  exclude_patterns: string[]
  blocked_modules: string[]
  chunk_size: number | null
  use_unmodified_previews: boolean
  search_backend: "azure-search" | "filth"
  filth_backend_id: string
  filth_scope: string
  filth_source_uids: string[]
  company_wide: boolean
  /** Server merges the stored auth of this library on save; typed non-empty values win. */
  copy_auth_from_library_id?: string
}

export type LibraryEditorErrors = Partial<
  Record<
    | keyof LibraryEditorValues
    | "sharepoint_url"
    | "input_container"
    | "environment_url"
    | "entity_name"
    | "extra_data_definition",
    string
  >
>

export type EditorSection =
  | "basics"
  | "source"
  | "connection"
  | "content"
  | "extraData"
  | "indexing"
  | "advanced"

/** Source types an admin can pick when creating. Legacy `spo` is edit-only (same shape as `sharepoint`). */
export const CREATABLE_SOURCE_TYPES = [
  "sharepoint",
  "spo-avtal",
  "spo-list",
  "spo-pages",
  "blob",
  "dynamics",
  "external",
] as const

export const EMPTY_EDITOR_VALUES: LibraryEditorValues = {
  id: "",
  display_name: "",
  description: "",
  source_type: "sharepoint",
  // Off by default until the #2671 E2E matrix has run against a real tenant
  // (product sign-off on #2672 F4) — admins opt in per library.
  source_config: { replicate_permissions: false },
  embedding_model_id: "",
  index_name: "",
  output_container: "",
  output_table: "",
  search_enabled: true,
  indexing_enabled: true,
  exclude_patterns: [],
  blocked_modules: [],
  chunk_size: null,
  use_unmodified_previews: false,
  search_backend: "azure-search",
  filth_backend_id: "",
  filth_scope: "",
  filth_source_uids: [],
  company_wide: false,
}

export function isSharePointFamily(sourceType: string): boolean {
  return (
    SHAREPOINT_COPY_SOURCE_TYPES.has(sourceType) ||
    sourceType === "spo" ||
    sourceType === "sharepoint"
  )
}

/** Document-library flavour (files, folders, permission replication). */
export function isDocumentLibraryType(sourceType: string): boolean {
  return sourceType === "sharepoint" || sourceType === "spo"
}

export function isListType(sourceType: string): boolean {
  return SHAREPOINT_LIST_SOURCE_TYPES.has(sourceType)
}

export function isPagesType(sourceType: string): boolean {
  return sourceType === "spo-pages"
}

export function isExternalType(sourceType: string): boolean {
  return sourceType === "external" || sourceType === "http"
}

/** Which sections apply to a source type. Basics, indexing and advanced always do. */
export function sectionsFor(sourceType: string): EditorSection[] {
  const sections: EditorSection[] = ["basics", "source"]
  if (isSharePointFamily(sourceType) || sourceType === "dynamics") sections.push("connection")
  if (!isExternalType(sourceType)) sections.push("content")
  if (isSharePointFamily(sourceType)) sections.push("extraData")
  sections.push("indexing", "advanced")
  return sections
}

/** Library ids: lowercase letters, digits and hyphens, derived from the display name. */
export function slugifyLibraryName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
}

export function defaultOutputNamesFor(id: string): {
  index_name: string
  output_container: string
  output_table: string
} {
  return {
    index_name: id,
    output_container: id ? `${id}-output` : "",
    output_table: id ? `${id.replace(/-/g, "")}index` : "",
  }
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : []
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

export function libraryToEditorValues(library: Library): LibraryEditorValues {
  const sourceConfig = asRecord(library.source_config) as LibrarySourceConfigDraft
  const raw = library as Library & { use_unmodified_previews?: boolean }
  return {
    id: library.id,
    display_name: library.display_name || "",
    description: library.description || "",
    source_type: library.source_type,
    source_config: { ...sourceConfig },
    embedding_model_id: library.embedding_model_id || "",
    index_name: library.index_name || "",
    output_container: library.output_container || "",
    output_table: library.output_table || "",
    search_enabled: library.search_enabled !== false,
    indexing_enabled: library.indexing_enabled !== false,
    exclude_patterns: asStringArray(library.exclude_patterns),
    blocked_modules: asStringArray(library.blocked_modules),
    chunk_size: typeof library.chunk_size === "number" ? library.chunk_size : null,
    use_unmodified_previews: raw.use_unmodified_previews === true,
    search_backend: library.search_backend === "filth" ? "filth" : "azure-search",
    filth_backend_id: library.filth_backend_id ?? "",
    filth_scope: library.filth_scope ?? "",
    filth_source_uids: asStringArray(library.filth_source_uids),
    company_wide: library.company_wide === true,
    copy_auth_from_library_id: undefined,
  }
}

/**
 * source_config as sent to the server. The pasted URL is UI-only and never stored. A field the
 * admin cleared holds `undefined` in the form: create simply omits it, update sends `null` so
 * the server drops the stored value (its merge keeps every key the payload leaves out). `auth`
 * is never sent as null: leaving it out keeps the stored credentials.
 */
function sourceConfigForSave(
  values: LibraryEditorValues,
  mode: "create" | "update"
): Record<string, unknown> {
  const { sharepoint_url: _url, ...rest } = values.source_config
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(rest)) {
    if (value !== undefined) out[key] = value
    else if (mode === "update" && key !== "auth") out[key] = null
  }
  return out
}

function copyAuthField(
  values: LibraryEditorValues
): Pick<LibraryConfig, "copy_auth_from_library_id"> {
  const id = values.copy_auth_from_library_id?.trim()
  return id ? { copy_auth_from_library_id: id } : {}
}

function filthFields(values: LibraryEditorValues): Partial<LibraryConfig> {
  if (values.search_backend !== "filth")
    return { search_backend: "azure-search", filth_source_uids: [] }
  return {
    search_backend: "filth",
    filth_backend_id: values.filth_backend_id,
    filth_scope: values.filth_scope || undefined,
    filth_source_uids: [...values.filth_source_uids],
    company_wide: values.company_wide,
  }
}

export function editorValuesToCreatePayload(values: LibraryEditorValues): LibraryConfig {
  const id = values.id.trim()
  const defaults = defaultOutputNamesFor(id)
  return {
    id,
    display_name: values.display_name.trim(),
    description: values.description,
    source_type: values.source_type,
    source_config: sourceConfigForSave(values, "create"),
    embedding_model_id: values.embedding_model_id,
    index_name: values.index_name || defaults.index_name,
    output_container: values.output_container || defaults.output_container,
    output_table: values.output_table || defaults.output_table,
    search_enabled: values.search_enabled,
    indexing_enabled: values.indexing_enabled,
    exclude_patterns: values.exclude_patterns,
    blocked_modules: values.blocked_modules,
    chunk_size: values.chunk_size,
    use_unmodified_previews: values.use_unmodified_previews,
    ...filthFields(values),
    ...copyAuthField(values),
  }
}

/**
 * Update payload: only fields the edit form owns. Index/container/table and the embedding model
 * are set at creation and stay read-only in edit (changing them orphans the indexed data).
 */
export function editorValuesToUpdatePayload(values: LibraryEditorValues): Partial<LibraryConfig> {
  return {
    display_name: values.display_name.trim(),
    description: values.description,
    source_config: sourceConfigForSave(values, "update"),
    search_enabled: values.search_enabled,
    indexing_enabled: values.indexing_enabled,
    exclude_patterns: values.exclude_patterns,
    blocked_modules: values.blocked_modules,
    chunk_size: values.chunk_size,
    use_unmodified_previews: values.use_unmodified_previews,
    ...filthFields(values),
    // PATCH must distinguish clearing scope from leaving the stored value alone.
    ...(values.search_backend === "filth" ? { filth_scope: values.filth_scope } : {}),
    ...copyAuthField(values),
  }
}

/** True when the stored secret is the server's mask, i.e. the admin has not typed a new name. */
export function hasStoredSecret(values: LibraryEditorValues): boolean {
  return values.source_config.auth?.client_secret === MASKED_SECRET_INDICATOR
}

export const LIBRARY_ID_PATTERN = /^[a-z0-9-]+$/
export const OUTPUT_TABLE_PATTERN = /^[a-z][a-z0-9]*$/
export const OUTPUT_CONTAINER_PATTERN = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/

// ----- target naming vs ids ---------------------------------------------------------------------

/**
 * source_config keys that name the target. Editing one retargets the library, so the ids (which
 * the server prefers over the names) are dropped with it: a resolved or stored list id would
 * otherwise keep pointing at the previous list.
 */
export const TARGET_NAME_KEYS: ReadonlySet<string> = new Set([
  "hostname",
  "siteName",
  "listName",
  "site_name",
  "list_name",
])

/** Every key shape the ids can live under. */
const TARGET_ID_KEYS = ["siteId", "listId", "site_id", "list_id", "drive_id"] as const

/** source_config with `key` set; a change to a name key also clears the ids. */
export function sourceConfigWithKey(
  sourceConfig: LibrarySourceConfigDraft,
  key: string,
  value: unknown
): LibrarySourceConfigDraft {
  const next: LibrarySourceConfigDraft = { ...sourceConfig, [key]: value }
  if (TARGET_NAME_KEYS.has(key)) {
    for (const idKey of TARGET_ID_KEYS) {
      if (idKey in next) next[idKey] = undefined
    }
  }
  return next
}

/**
 * source_config with the ids from inspect-sharepoint, written under whichever key shape it
 * already uses. A generic list has no drive, so a drive id left over from a document library is
 * cleared rather than kept.
 */
export function sourceConfigWithResolvedIds(
  sourceConfig: LibrarySourceConfigDraft,
  result: SpoInspectResult
): LibrarySourceConfigDraft {
  const next: LibrarySourceConfigDraft = { ...sourceConfig }
  next[spoFieldStorageKey(next, "siteId")] = result.site_id
  next[spoFieldStorageKey(next, "listId")] = result.list_id
  next.drive_id = result.drive_id ?? undefined
  return next
}

/** Leading and trailing slashes never make it into a stored folder path. */
export function normalizeFolderPath(text: string): string {
  return text.replace(/^\/+|\/+$/g, "")
}
